import type { Candidate, GeoHint, PeerId, SegmentId } from "@p2p-hls/protocol";
import { Emitter } from "./emitter.js";
import { Peer } from "./peer.js";
import type { SignalingClient } from "./signaling-client.js";
import type { EngineConfig } from "./types.js";

interface Connection {
  readonly pc: RTCPeerConnection;
  readonly peer: Peer;
  remoteSet: boolean;
  readonly pendingIce: RTCIceCandidateInit[];
}

type ManagerEvents = {
  "peer-open": Peer;
  "peer-closed": PeerId;
};

/**
 * Builds and tears down the WebRTC mesh, translating tracker signaling into
 * RTCPeerConnections. To avoid double-offer races, only the JOINING peer (the
 * one that received the candidate list) initiates offers; existing peers answer.
 */
export class PeerConnectionManager extends Emitter<ManagerEvents> {
  private readonly conns = new Map<PeerId, Connection>();
  private lastRefresh = 0;
  /** Ask the tracker for a fresh candidate sample when the mesh thins out.
   * Replaces the removed O(N²) peer-gone broadcast: instead of the tracker
   * telling everyone about every departure, a viewer re-samples only when its
   * own connection count drops. Idempotent + throttled. */
  private maybeRefresh(): void {
    if (this.conns.size >= Math.ceil(this.config.maxPeers / 2)) return;
    const now = Date.now();
    if (now - this.lastRefresh < 5000) return;
    this.lastRefresh = now;
    this.signaling.join(); // idempotent → tracker replies with a fresh candidate sample
  }

  constructor(
    private readonly config: EngineConfig,
    private readonly signaling: SignalingClient,
    /** Serve locally-held bytes to a requesting peer. */
    private readonly serve: (seg: SegmentId) => Uint8Array | undefined,
    /** Forward each peer's RTT samples to the metrics layer. */
    private readonly onRtt: (peerId: PeerId, ms: number) => void = () => {},
  ) {
    super();
    signaling.on("peers", (msg) => {
      for (const c of msg.candidates) void this.connectTo(c);
    });
    signaling.on("signal", (s) => void this.onSignal(s.from, s.data, s.fromGeo));
    signaling.on("peer-gone", (id) => this.drop(id));
  }

  peers(): Peer[] {
    return [...this.conns.values()].map((c) => c.peer);
  }

  // ---- outbound (offerer) ---------------------------------------------------

  private async connectTo(candidate: Candidate): Promise<void> {
    if (!webrtcSupported()) return; // degrade to HTTP-only
    if (this.conns.has(candidate.peerId)) return;
    if (this.conns.size >= this.config.maxPeers) return;

    const pc = new RTCPeerConnection({ iceServers: this.config.iceServers });
    const peer = new Peer(candidate.peerId, candidate.geo, pc, this.serve, {
      onRtt: this.onRtt,
      serveDelayMs: this.config.debugServeDelayMs,
    });
    // Unordered + reliable: we reassemble segment chunks by index, so ordering
    // is unnecessary — dropping it removes head-of-line blocking (throughput ↑).
    const channel = pc.createDataChannel("p2p", { ordered: false });
    peer.attachChannel(channel);
    this.register(candidate.peerId, pc, peer);

    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    this.signaling.relaySignal(candidate.peerId, { kind: "offer", sdp: offer.sdp! });
  }

  // ---- inbound (answerer / negotiation) -------------------------------------

  private async onSignal(
    from: PeerId,
    data: { kind: "offer" | "answer" | "ice"; sdp?: string; candidate?: RTCIceCandidateInit },
    fromGeo: GeoHint,
  ): Promise<void> {
    if (!webrtcSupported()) return; // degrade to HTTP-only
    if (data.kind === "offer") {
      let conn = this.conns.get(from);
      if (!conn) {
        if (this.conns.size >= this.config.maxPeers) return;
        const pc = new RTCPeerConnection({ iceServers: this.config.iceServers });
        const peer = new Peer(from, fromGeo, pc, this.serve, {
          onRtt: this.onRtt,
          serveDelayMs: this.config.debugServeDelayMs,
        });
        pc.addEventListener("datachannel", (ev) => peer.attachChannel(ev.channel));
        conn = this.register(from, pc, peer);
      }
      await conn.pc.setRemoteDescription({ type: "offer", sdp: data.sdp! });
      conn.remoteSet = true;
      await this.flushIce(conn);
      const answer = await conn.pc.createAnswer();
      await conn.pc.setLocalDescription(answer);
      this.signaling.relaySignal(from, { kind: "answer", sdp: answer.sdp! });
    } else if (data.kind === "answer") {
      const conn = this.conns.get(from);
      if (!conn) return;
      await conn.pc.setRemoteDescription({ type: "answer", sdp: data.sdp! });
      conn.remoteSet = true;
      await this.flushIce(conn);
    } else if (data.kind === "ice" && data.candidate) {
      const conn = this.conns.get(from);
      if (!conn) return;
      // ICE can arrive before the remote description is set — buffer until ready.
      if (conn.remoteSet) await conn.pc.addIceCandidate(data.candidate).catch(() => {});
      else conn.pendingIce.push(data.candidate);
    }
  }

  private async flushIce(conn: Connection): Promise<void> {
    for (const cand of conn.pendingIce.splice(0)) {
      await conn.pc.addIceCandidate(cand).catch(() => {});
    }
  }

  // ---- bookkeeping ----------------------------------------------------------

  private register(id: PeerId, pc: RTCPeerConnection, peer: Peer): Connection {
    const conn: Connection = { pc, peer, remoteSet: false, pendingIce: [] };
    this.conns.set(id, conn);

    pc.addEventListener("icecandidate", (ev) => {
      if (ev.candidate) this.signaling.relaySignal(id, { kind: "ice", candidate: ev.candidate.toJSON() });
    });
    peer.on("close", () => this.drop(id));
    // Surface the peer once its DataChannel is usable so the engine can register it.
    let announced = false;
    const un = peer.on("have", () => {
      if (announced) return;
      announced = true;
      un();
      this.emit("peer-open", peer);
    });
    return conn;
  }

  private drop(id: PeerId): void {
    const conn = this.conns.get(id);
    if (!conn) return;
    this.conns.delete(id);
    conn.peer.close();
    this.emit("peer-closed", id);
    this.maybeRefresh(); // top up the mesh if it thinned out
  }

  closeAll(): void {
    for (const id of [...this.conns.keys()]) this.drop(id);
  }
}

/** True if the browser can form WebRTC peer connections (else HTTP-only). */
export function webrtcSupported(): boolean {
  return typeof RTCPeerConnection !== "undefined";
}
